import { useContext, useMemo } from "react"
import PropTypes from "prop-types"
import { capitalize } from "lodash"
import { DESC } from "helpers/sorts"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { getContentSetMetrics } from "helpers/metrics"
import { useTemplateGatherers } from "../../../dashboard/hooks/use-template-gatherers"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"
import { MODEL_METADATA_SHAPE, INSTRUMENT_METADATA_SHAPE, FLATTENED_METRIC_SHAPE, INSTRUMENT_COLUMNS } from "../../prop-types"
import { CONTENT_SET_CONTEXT } from "./context"

export default function AssetViewContentSet({ modelMetadata, context, instrumentsWithMetadata, highlightedRowIds, disableActiveTrading, flattenedMetrics, instrumentColumns }) {
  const contentSetContext = useContext(CONTENT_SET_CONTEXT)
  const assetViewContext = useContext(context)
  const { selectedCategory, selectedMetricDisplay } = contentSetContext
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    view: contentSetId,
    isEndVsStartDateComparison,
  } = assetViewContext
  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })

  const contentSetMetrics = useMemo(() => getContentSetMetrics(contentSetId, flattenedMetrics, selectedCategory, selectedMetricDisplay), [contentSetId, flattenedMetrics, selectedCategory, selectedMetricDisplay])

  const gatherers = useTemplateGatherers(
    context,
    "contentSet",
    {
      selectedCategory,
      contentSetId,
      selectedMetricDisplay,
    },
  )

  const metrics = useAssetViewDashboardMetrics(
    [{ category: "contentSet", items: contentSetMetrics }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, selectedMetricDisplay, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]]?.suffix

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      downloadKey: "assets.content-set",
      downloadName: "Assets - Content Set",
      // Analytics callbacks
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      context,
    }}
  />
}

AssetViewContentSet.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithMetadata: PropTypes.objectOf(INSTRUMENT_METADATA_SHAPE).isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  selectedMetrics: PropTypes.arrayOf(PropTypes.string).isRequired,
  flattenedMetrics: PropTypes.arrayOf(FLATTENED_METRIC_SHAPE).isRequired,
  instrumentColumns: INSTRUMENT_COLUMNS.isRequired,
}
